import { daysBetween } from '@/utils/date/saturdayWindow';

/**
 * Filter Validation Utility
 *
 * Provides comprehensive validation for all filter inputs from URL parameters,
 * localStorage, and ibeSearchDefaults. All validation methods return null on
 * invalid input to enable graceful fallback to defaults.
 *
 * @example
 * ```ts
 * import { FilterValidation } from '@/utils/validation/filterValidation';
 *
 * // Validate duration
 * const duration = FilterValidation.validateDuration('7'); // 7
 * const invalid = FilterValidation.validateDuration('abc'); // null (logs warning)
 *
 * // Validate date
 * const date = FilterValidation.validateDate('2025-10-30'); // Date object
 * const relativeDate = FilterValidation.validateDate('+7'); // Date (7 days from now)
 *
 * // Sanitize user input
 * const safe = FilterValidation.sanitizeAttribute('<script>alert("xss")</script>'); // 'scriptalert(xss)/script'
 * ```
 */
export class FilterValidation {
  private static readonly MAX_DURATION = 365; // 1 year max
  private static readonly MIN_DURATION = 1;
  private static readonly MAX_PASSENGER_AGE = 120;
  private static readonly MIN_PASSENGER_AGE = 0;
  private static readonly MAX_DESTINATION_ID_LENGTH = 50;
  private static readonly MAX_PAGE = 1000;
  private static readonly MIN_PAGE = 1;

  /**
   * Validate duration value
   */
  static validateDuration(value: unknown): number | null {
    const duration = Number(value);

    if (isNaN(duration)) {
      console.warn(`[FilterValidation] Invalid duration (not a number): ${value}`);
      return null;
    }

    if (duration < this.MIN_DURATION || duration > this.MAX_DURATION) {
      console.warn(
        `[FilterValidation] Duration out of range: ${duration} (must be ${this.MIN_DURATION}-${this.MAX_DURATION})`
      );
      return null;
    }

    return Math.floor(duration); // Ensure integer
  }

  /**
   * Validate date string and return Date object
   */
  static validateDate(dateStr: string | unknown): Date | null {
    if (typeof dateStr !== 'string') {
      console.warn(`[FilterValidation] Invalid date (not a string): ${dateStr}`);
      return null;
    }

    try {
      // Handle relative dates (+N)
      if (dateStr.startsWith('+')) {
        const days = parseInt(dateStr.substring(1), 10);
        if (isNaN(days) || days < 0) {
          console.warn(`[FilterValidation] Invalid relative date: ${dateStr}`);
          return null;
        }
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        date.setDate(date.getDate() + days);
        return date;
      }

      // Handle absolute dates (YYYY-MM-DD)
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
      if (!match) {
        console.warn(`[FilterValidation] Invalid date format: ${dateStr}`);
        return null;
      }
      const y = Number(match[1]);
      const m = Number(match[2]);
      const day = Number(match[3]);
      const date = new Date(y, m - 1, day);
      if (
        !this.isValidDate(date) ||
        date.getFullYear() !== y ||
        date.getMonth() !== m - 1 ||
        date.getDate() !== day
      ) {
        console.warn(`[FilterValidation] Invalid date format: ${dateStr}`);
        return null;
      }

      return date;
    } catch (error) {
      console.warn(`[FilterValidation] Error parsing date: ${dateStr}`, error);
      return null;
    }
  }

  /**
   * Check if Date object is valid
   */
  static isValidDate(date: Date): boolean {
    return !isNaN(date.getTime());
  }

  /**
   * Validate date range (start must be before end)
   */
  static validateDateRange(startDate: Date | null, endDate: Date | null): boolean {
    if (!startDate || !endDate) return false;
    if (!this.isValidDate(startDate) || !this.isValidDate(endDate)) return false;
    return startDate < endDate;
  }

  /**
   * Check that a start date is acceptable as a filter input
   */
  static isStartDateBookable(date: Date | null): boolean {
    if (!date || !this.isValidDate(date)) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }

  /**
   * Validate that duration fits within the date range. Uses the shared
   * {@link daysBetween} (local-midnight, DST-safe) so the whole-day count agrees
   * exactly with `nightsInRange` and the Saturday-window widening.
   */
  static validatePeriodConsistency(startDate: Date, endDate: Date, duration: number): boolean {
    return duration <= daysBetween(startDate, endDate);
  }

  /**
   * Validate passenger age
   */
  static validatePassengerAge(age: unknown): number | null {
    const ageNum = Number(age);

    if (isNaN(ageNum)) {
      console.warn(`[FilterValidation] Invalid passenger age (not a number): ${age}`);
      return null;
    }

    if (ageNum < this.MIN_PASSENGER_AGE || ageNum > this.MAX_PASSENGER_AGE) {
      console.warn(`[FilterValidation] Passenger age out of range: ${ageNum}`);
      return null;
    }

    return Math.floor(ageNum);
  }

  /**
   * Validate destination ID (basic sanity check)
   */
  static validateDestinationId(id: unknown): string | null {
    if (typeof id !== 'string') {
      console.warn(`[FilterValidation] Invalid destination ID (not a string): ${id}`);
      return null;
    }

    const trimmed = id.trim();
    if (trimmed.length === 0 || trimmed.length > this.MAX_DESTINATION_ID_LENGTH) {
      console.warn(`[FilterValidation] Invalid destination ID length: ${trimmed}`);
      return null;
    }

    return trimmed;
  }

  /**
   * Validate sort option
   */
  static validateSortBy(sort: unknown): 'price' | 'recommendation' | null {
    if (typeof sort !== 'string') return null;

    const normalized = sort.toLowerCase();
    if (normalized === 'price') return 'price';
    if (normalized === 'reco' || normalized === 'recommendation') return 'recommendation';

    console.warn(`[FilterValidation] Invalid sort value: ${sort}`);
    return null;
  }

  /**
   * Sanitize attribute key/value for XSS protection
   */
  static sanitizeAttribute(value: string): string {
    // Remove potentially dangerous characters: < > ' "
    return value.replace(/[<>'"]/g, '').trim();
  }

  /**
   * Validate product type string
   */
  static validateProductType(type: unknown): string | null {
    if (typeof type !== 'string') {
      return null;
    }

    const trimmed = type.trim();
    if (trimmed.length === 0 || trimmed.length > 50) {
      return null;
    }

    return trimmed;
  }

  /**
   * Validate array of product types
   */
  static validateProductTypes(types: unknown[]): string[] {
    return types
      .map((type) => this.validateProductType(type))
      .filter((type): type is string => type !== null);
  }

  /**
   * Validate array of destination IDs
   */
  static validateDestinationIds(ids: unknown[]): string[] {
    return ids
      .map((id) => this.validateDestinationId(id))
      .filter((id): id is string => id !== null);
  }

  /**
   * Validate page number
   */
  static validatePage(value: unknown): number | null {
    const page = Number(value);

    if (isNaN(page)) {
      console.warn(`[FilterValidation] Invalid page number (not a number): ${value}`);
      return null;
    }

    if (page < this.MIN_PAGE || page > this.MAX_PAGE) {
      console.warn(
        `[FilterValidation] Page number out of range: ${page} (must be ${this.MIN_PAGE}-${this.MAX_PAGE})`
      );
      return null;
    }

    return Math.floor(page); // Ensure integer
  }
}
